import React, { FC } from 'react';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import { useNFT, useContract } from '@thirdweb-dev/react';
import { AspectRatio, Box, HStack, Text } from '@chakra-ui/react';
import { SellPrice } from '@app/screens/nft-marketplace/SellPrice';
import { RewardCardAction } from '@app/components/RewardCard/RewardCardAction';
import { useRewardNftsMarketplaceFiltersState } from '@app/state/rewardsNftsMarketplaceFiltersStore';
import { useFindTokenInWhitelist } from '@app/hooks/nft/useFindTokenInWhitelist';
import { useHandleUsdPriceReady } from '@app/hooks/nft/useHandleUsdPriceReady';

type Props = {
  listing: NFTMarketplaceListing;
  isMobile?: boolean;
};

export const RewardCard: FC<Props> = ({ listing, isMobile }) => {
  const { contract } = useContract(listing.nftContractAddress);
  const { data: nft, isLoading } = useNFT(contract, listing.tokenId);
  const {
    floorPrices,
    actions: { updateFloorPrices }
  } = useRewardNftsMarketplaceFiltersState();

  const sellCurrency = useFindTokenInWhitelist(listing);

  const handleUsdPriceReady = useHandleUsdPriceReady(
    listing,
    floorPrices,
    updateFloorPrices
  );

  if (isLoading) {
    return;
  }

  if (!nft || !sellCurrency) {
    return null;
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'neutral.700',
        borderRadius: '8px',
        padding: '12px',
        width: 'fit-content',
        minWidth: '200px',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        flex: '1',
        height: '100%'
      }}
      _hover={{
        borderColor: 'orange'
      }}
      data-group
    >
      <Box top={0}>
        <AspectRatio
          ratio={1}
          w="100%"
          pos="relative"
          overflow="hidden"
          minWidth={200}
          borderRadius="8px"
        >
          {nft.metadata.image ? (
            <img
              src={nft.metadata.image}
              alt={nft.metadata.name?.toString() ?? ''}
            />
          ) : (
            <div>placeholder</div>
          )}
        </AspectRatio>
        <Text fontSize={14} fontWeight={500} mt={2} color="neutral.100">
          {nft.metadata.name}
        </Text>
        <HStack
          alignItems="flex-start"
          justifyContent="space-between"
          mt={2}
          width="100%"
        >
          <Text fontSize={12} color="neutral.300">
            Price
          </Text>
          <SellPrice
            currency={sellCurrency}
            listing={listing}
            onUsdPriceReady={handleUsdPriceReady}
          />
        </HStack>
      </Box>
      <RewardCardAction listing={listing} sellCurrency={sellCurrency} />
    </Box>
  );
};
